import { useState, useEffect } from "react";
import { Form } from "react-bootstrap";
import { toast } from 'react-toastify';
import {
    getProductTrades,
    getProductsByTradeId,
    getFeedTypeByTradeAndProduct,
    getSpeciesByTradeAndProductAndFeed,
    getProductConfiguration,
    getUnits
} from "./AssayRequestRecordApi";
import "../../../styles/typography.css";
import "./AssayEditPage.css";

const AssayEditPage = ({
    data,
    onDataChange,
    productTypes = [],
    productTrades = [],
    feedTypes = [],
    units = [],
    species = [],
    locations = [],
    userRole = null,
    validationErrors = {},
    setValidationErrors
}) => {
    const [formData, setFormData] = useState({});
    const [samples, setSamples] = useState([]);
    const [userDetails, setUserDetails] = useState({
        companyName: "",
        locationName: "",
        locationId: "",
        salesRep: ""
    });
    const [isInitialized, setIsInitialized] = useState(false);

    const [availableProductTrades, setAvailableProductTrades] = useState([]);
    const [availableProductTypes, setAvailableProductTypes] = useState({});
    const [availableFeedTypes, setAvailableFeedTypes] = useState({});
    const [availableSpecies, setAvailableSpecies] = useState({});
    const [availableUnits, setAvailableUnits] = useState([]);

    const validateExpectedLevel = (value) => {
        if (!value || value.trim() === "") {
            return "Expected Level is required";
        }
        return null;
    };

    const validateField = (sampleId, fieldName, value) => {
        const errors = { ...validationErrors };
        const fieldKey = `${sampleId}_${fieldName}`;
        delete errors[fieldKey];

        const requiredFields = [
            'productTradeName', 'productType', 'feedType', 'species',
            'unit', 'expectedLevel', 'phibroLots', 'customerSampleId'
        ];

        if (requiredFields.includes(fieldName)) {
            if (!value || (typeof value === "string" && value.trim() === "")) {
                errors[fieldKey] = `${getFieldLabel(fieldName)} is required`;
            } else if (fieldName === 'expectedLevel') {
                const errorMessage = validateExpectedLevel(value);
                if (errorMessage) {
                    errors[fieldKey] = errorMessage;
                }
            }
        }
        setValidationErrors(errors);
        return !errors[fieldKey];
    };

    const handleExpectedLevelChange = (sampleId, value) => {
        return value;
    };

    const getFieldLabel = (fieldName) => {
        const labels = {
            'productTradeName': 'Product Trade Name',
            'productType': 'Product Type',
            'feedType': 'Feed Type',
            'species': 'Species',
            'unit': 'Unit',
            'expectedLevel': 'Expected Level',
            'phibroLots': 'Phibro Lots',
            'customerSampleId': 'Customer Sample ID',
            'referenceNo': 'Reference No',
            'cfrMinLevel': 'CFR Min Level',
            'cfrMaxLevel': 'CFR Max Level',
            'loqValue': 'LOQ Value'
        };
        return labels[fieldName] || fieldName;
    };

    useEffect(() => {
        const initializeDependentDropdowns = async () => {
            try {
                const [productTradesRes, unitsRes] = await Promise.all([
                    getProductTrades(),
                    getUnits()
                ]);
                setAvailableProductTrades(productTradesRes || []);
                setAvailableUnits(unitsRes || []);
            } catch (error) {
                console.error("Failed to initialize dependent dropdowns:", error);
            }
        };
        initializeDependentDropdowns();
    }, []);

    useEffect(() => {
        if (data && !isInitialized) {
            setFormData(data);
            const newUserDetails = {
                companyName: data.company || data.companyName || userRole?.companyName || "Tetley",
                locationName: data.location || data.locationName || "State College",
                locationId: data.locationId || data.locationID || userRole?.locationID || "",
                salesRep: data.phibroRepName || data.salesRep || userRole?.salesRep || "Abigail Clancy"
            };
            setUserDetails(newUserDetails);

            let samplesList = [];
            if (data.samples && Array.isArray(data.samples)) {
                samplesList = data.samples.map((sample, index) => ({
                    ...sample,
                    id: sample.id || sample.sampleId || sample.sampleID || (index + 1),
                    expanded: true,
                    productTradeName: sample.productTradeNameId?.toString() || "",
                    productType: sample.productTypeId?.toString() || "",
                    feedType: sample.feedTypeId?.toString() || "",
                    species: sample.speciesId?.toString() || "",
                    unit: sample.unitId?.toString() || "",
                    expectedLevel: sample.expectedLevel?.toString() || "",
                    phibroLots: sample.lotNumber || sample.phibroLots || "",
                    customerSampleId: sample.customerSampleId || "",
                    referenceNo: sample.referenceNo || "",
                    remarks: sample.remarks || "",
                    cfrMinLevel: sample.cfrMinLevel || "",
                    cfrMaxLevel: sample.cfrMaxLevel || "",
                    loqValue: sample.loqValue || "",
                    barcodeNumber: sample.barcodeNumber || ""
                }));
            } else {
                samplesList = [{
                    id: data.sampleId || data.sampleID || data.id || 1,
                    expanded: true,
                    productTradeName: data.productTradeNameId?.toString() || "",
                    productType: data.productTypeId?.toString() || "",
                    feedType: data.feedTypeId?.toString() || "",
                    species: data.speciesId?.toString() || "",
                    unit: data.unitId?.toString() || "",
                    expectedLevel: data.expectedLevel?.toString() || "",
                    phibroLots: data.lotNumber || data.phibroLots || "",
                    customerSampleId: data.customerSampleId || "",
                    referenceNo: data.referenceNo || "",
                    remarks: data.remarks || "",
                    cfrMinLevel: data.cfrMinLevel || "",
                    cfrMaxLevel: data.cfrMaxLevel || "",
                    loqValue: data.loqValue || "",
                    barcodeNumber: data.barcodeNumber || ""
                }];
            }
            setSamples(samplesList);
            setIsInitialized(true);

            samplesList.forEach(sample => {
                if (sample.productTradeName) {
                    loadProductTypes(sample.productTradeName, sample.id);
                }
                if (sample.productTradeName && sample.productType) {
                    loadFeedTypes(sample.productTradeName, sample.productType, sample.id);
                }
                if (sample.productTradeName && sample.productType && sample.feedType) {
                    loadSpecies(sample.productTradeName, sample.productType, sample.feedType, sample.id);
                    loadProductConfiguration(sample.productTradeName, sample.productType, sample.feedType, sample.species, sample.id);
                }
            });
        }
    }, [data, userRole, isInitialized]);

    const loadProductTypes = async (productTradeId, sampleId) => {
        if (!productTradeId) return;
        try {
            const productTypesData = await getProductsByTradeId(productTradeId);
            setAvailableProductTypes(prev => ({
                ...prev,
                [sampleId]: productTypesData || []
            }));
        } catch (error) {
            console.error("Failed to load product types:", error);
            setAvailableProductTypes(prev => ({
                ...prev,
                [sampleId]: []
            }));
        }
    };

    const loadFeedTypes = async (productTradeId, productTypeId, sampleId) => {
        if (!productTradeId || !productTypeId) return;
        try {
            const feedTypesData = await getFeedTypeByTradeAndProduct(productTradeId, productTypeId);
            setAvailableFeedTypes(prev => ({
                ...prev,
                [sampleId]: feedTypesData || []
            }));
        } catch (error) {
            console.error("Failed to load feed types:", error);
            setAvailableFeedTypes(prev => ({
                ...prev,
                [sampleId]: []
            }));
        }
    };

    const loadSpecies = async (productTradeId, productTypeId, feedTypeId, sampleId) => {
        if (!productTradeId || !productTypeId || !feedTypeId) return;
        try {
            const speciesData = await getSpeciesByTradeAndProductAndFeed(productTradeId, productTypeId, feedTypeId);
            setAvailableSpecies(prev => ({
                ...prev,
                [sampleId]: speciesData || []
            }));
        } catch (error) {
            console.error("Failed to load species:", error);
        }
    };

    const loadProductConfiguration = async (productTradeId, productTypeId, feedTypeId, speciesId, sampleId) => {
        if (!productTradeId || !productTypeId || !feedTypeId || !speciesId) return;
        try {
            const selectedSpecies = availableSpecies[sampleId]?.find(s => s.speciesId == speciesId);
            const configData = await getProductConfiguration(productTradeId, productTypeId, feedTypeId, selectedSpecies?.masterSpeciesId);
            setSamples(prev => prev.map(sample =>
                sample.id === sampleId ? {
                    ...sample,
                    cfrMinLevel: configData?.cfrMinLevel ?? sample.cfrMinLevel ?? "",
                    cfrMaxLevel: configData?.cfrMaxLevel ?? sample.cfrMaxLevel ?? "",
                    loqValue: configData?.loqValue ?? sample.loqValue ?? ""
                } : sample
            ));
        } catch (error) {
            console.error("Failed to load product configuration:", error);
        }
    };

    const handleSampleChange = async (sampleId, field, value) => {
        let processedValue = value;
        if (field === 'expectedLevel') {
            processedValue = handleExpectedLevelChange(sampleId, value);
        }
        validateField(sampleId, field, processedValue);

        const updatedSamples = samples.map(sample =>
            sample.id === sampleId ? { ...sample, [field]: processedValue } : sample
        );

        setSamples(updatedSamples);

        const currentSample = updatedSamples.find(s => s.id === sampleId);

        if (field === 'productTradeName') {
            const resetSample = {
                ...currentSample,
                productType: "",
                feedType: "",
                species: "",
                cfrMinLevel: "",
                cfrMaxLevel: "",
                loqValue: ""
            };
            setSamples(prev => prev.map(s => s.id === sampleId ? resetSample : s));
            if (processedValue) {
                await loadProductTypes(processedValue, sampleId);
            }
            setAvailableFeedTypes(prev => ({ ...prev, [sampleId]: [] }));
            setAvailableSpecies(prev => ({ ...prev, [sampleId]: [] }));

        } else if (field === 'productType') {
            const resetSample = {
                ...currentSample,
                feedType: "",
                species: "",
                cfrMinLevel: "",
                cfrMaxLevel: "",
                loqValue: ""
            };
            setSamples(prev => prev.map(s => s.id === sampleId ? resetSample : s));
            if (processedValue && currentSample.productTradeName) {
                await loadFeedTypes(currentSample.productTradeName, processedValue, sampleId);
            }
            setAvailableSpecies(prev => ({ ...prev, [sampleId]: [] }));

        } else if (field === 'feedType') {
            const resetSample = {
                ...currentSample,
                species: "",
                cfrMinLevel: "",
                cfrMaxLevel: "",
                loqValue: ""
            };
            setSamples(prev => prev.map(s => s.id === sampleId ? resetSample : s));
            if (processedValue && currentSample.productTradeName && currentSample.productType) {
                await loadSpecies(currentSample.productTradeName, currentSample.productType, processedValue, sampleId);
            }

        } else if (field === 'species') {
            const resetSample = {
                ...currentSample,
                cfrMinLevel: "",
                cfrMaxLevel: "",
                loqValue: "",
                species: processedValue
            };
            setSamples(prev => prev.map(s => s.id === sampleId ? resetSample : s));
            if (processedValue && currentSample.productTradeName && currentSample.productType && currentSample.feedType) {
                await loadProductConfiguration(currentSample.productTradeName, currentSample.productType, currentSample.feedType, processedValue, sampleId);
            }
        }

        const updatedFormData = { ...formData, samples: updatedSamples };
        setFormData(updatedFormData);
        onDataChange?.(updatedFormData);
    };

    const handleExpandToggle = (sampleId) => {
        const updatedSamples = samples.map(sample =>
            sample.id === sampleId ? { ...sample, expanded: !sample.expanded } : sample
        );
        setSamples(updatedSamples);
    };

    const hasFieldError = (sampleId, fieldName) => {
        return !!validationErrors[`${sampleId}_${fieldName}`];
    };

    const getFieldError = (sampleId, fieldName) => {
        return validationErrors[`${sampleId}_${fieldName}`] || "";
    };

    const isSpeciesSelectionValid = (sample) => {
        if (
            (sample.productTradeName && !sample.species) ||
            (sample.productType && !sample.species) ||
            (sample.feedType && !sample.species)
        ) {
            return false;
        }
        return true;
    };

    const handleSubmit = () => {
        for (const sample of samples) {
            if (!isSpeciesSelectionValid(sample)) {
                toast.error("Species selection is mandatory when Product Trade Name, Product Type, or Feed Type is selected.");
                return;
            }
        }
        toast.success("Form submitted successfully!");
    };

    return (
        <Form>
            <div className="assay-edit-company-info">
                <div className="row g-3">
                    <div className="col-md-4">
                        <label className="form-label">Company Name</label>
                        <input
                            type="text"
                            className="form-control"
                            value={userDetails.companyName}
                            disabled
                            title="Company name"
                            style={{ backgroundColor: "#f8f9fa" }}
                        />
                    </div>
                    <div className="col-md-4">
                        <label className="form-label">Location</label>
                        <input
                            type="text"
                            className="form-control"
                            value={userDetails.locationName}
                            disabled
                            title="Location name"
                            style={{ backgroundColor: "#f8f9fa" }}
                        />
                    </div>
                    <div className="col-md-4">
                        <label className="form-label">Select Phibro Rep</label>
                        <input
                            type="text"
                            className="form-control"
                            value={userDetails.salesRep}
                            disabled
                            title="Phibro representative"
                            style={{ backgroundColor: "#f8f9fa" }}
                        />
                    </div>
                </div>
            </div>

            <div className="assay-edit-customer-sample mt-4">
                {samples.map((sample, idx) => (
                    <div key={sample.id} className="assay-edit-sample-block mb-3">
                        <div className="assay-edit-sample-header-bar">
                            <span className="assay-edit-sample-title">
                                {idx + 1}. Customer Sample ID: {sample.customerSampleId}
                            </span>
                            <div className="assay-edit-sample-actions">
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-secondary"
                                    onClick={() => handleExpandToggle(sample.id)}
                                    title={sample.expanded ? "Collapse sample" : "Expand sample"}
                                >
                                    {sample.expanded ? "▲" : "▼"}
                                </button>
                            </div>
                        </div>

                        {sample.expanded && (
                            <div className="assay-edit-sample-content p-3">
                                <div className="row g-3 mb-3">
                                    <div className="col-md-4">
                                        <label className="form-label">Product Trade Name <span className="text-danger">*</span></label>
                                        <select
                                            className={`form-select ${hasFieldError(sample.id, 'productTradeName') ? 'is-invalid' : ''}`}
                                            value={sample.productTradeName || ""}
                                            onChange={(e) => handleSampleChange(sample.id, "productTradeName", e.target.value)}
                                            onBlur={(e) => validateField(sample.id, "productTradeName", e.target.value)}
                                            title="Select product trade name"
                                        >
                                            <option value="">Select Product Trade Name</option>
                                            {availableProductTrades.map((trade) => (
                                                <option
                                                    key={trade.productTradeNameId}
                                                    value={String(trade.productTradeNameId)}
                                                >
                                                    {trade.productTradeName}
                                                </option>
                                            ))}
                                        </select>
                                        {hasFieldError(sample.id, 'productTradeName') && (
                                            <div className="invalid-feedback">{getFieldError(sample.id, 'productTradeName')}</div>
                                        )}
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label">Product Type <span className="text-danger">*</span></label>
                                        <select
                                            className={`form-select ${hasFieldError(sample.id, 'productType') ? 'is-invalid' : ''}`}
                                            value={sample.productType || ""}
                                            onChange={(e) => handleSampleChange(sample.id, "productType", e.target.value)}
                                            onBlur={(e) => validateField(sample.id, "productType", e.target.value)}
                                            disabled={!sample.productTradeName}
                                            title="Select product type"
                                        >
                                            <option value="">Select Product Type</option>
                                            {(availableProductTypes[sample.id] || []).map((type) => (
                                                <option key={type.productTypeId} value={String(type.productTypeId)}>
                                                    {type.name}
                                                </option>
                                            ))}
                                        </select>
                                        {hasFieldError(sample.id, 'productType') && (
                                            <div className="invalid-feedback">{getFieldError(sample.id, 'productType')}</div>
                                        )}
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label">Feed Type <span className="text-danger">*</span></label>
                                        <select
                                            className={`form-select ${hasFieldError(sample.id, 'feedType') ? 'is-invalid' : ''}`}
                                            value={sample.feedType || ""}
                                            onChange={(e) => handleSampleChange(sample.id, "feedType", e.target.value)}
                                            onBlur={(e) => validateField(sample.id, "feedType", e.target.value)}
                                            disabled={!sample.productType}
                                            title="Select feed type"
                                        >
                                            <option value="">Select Feed Type</option>
                                            {(availableFeedTypes[sample.id] || []).map((feed) => (
                                                <option key={feed.feedTypeId} value={String(feed.feedTypeId)}>
                                                    {feed.feedTypeName}
                                                </option>
                                            ))}
                                        </select>
                                        {hasFieldError(sample.id, 'feedType') && (
                                            <div className="invalid-feedback">{getFieldError(sample.id, 'feedType')}</div>
                                        )}
                                    </div>
                                </div>

                                <div className="row g-3 mb-3">
                                    <div className="col-md-4">
                                        <label className="form-label">Species <span className="text-danger">*</span></label>
                                        <select
                                            className={`form-select ${hasFieldError(sample.id, 'species') ? 'is-invalid' : ''}`}
                                            value={sample.species || ""}
                                            onChange={(e) => handleSampleChange(sample.id, "species", e.target.value)}
                                            onBlur={(e) => validateField(sample.id, "species", e.target.value)}
                                            disabled={!sample.feedType}
                                            title="Select species"
                                        >
                                            <option value="">Select Species</option>
                                            {(availableSpecies[sample.id] || []).map((speciesItem) => (
                                                <option key={speciesItem.speciesId} value={String(speciesItem.speciesId)}>
                                                    {speciesItem.speciesName}
                                                </option>
                                            ))}
                                        </select>
                                        {hasFieldError(sample.id, 'species') && (
                                            <div className="invalid-feedback">{getFieldError(sample.id, 'species')}</div>
                                        )}
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label">Unit <span className="text-danger">*</span></label>
                                        <select
                                            className={`form-select ${hasFieldError(sample.id, 'unit') ? 'is-invalid' : ''}`}
                                            value={sample.unit || ""}
                                            onChange={(e) => handleSampleChange(sample.id, "unit", e.target.value)}
                                            onBlur={(e) => validateField(sample.id, "unit", e.target.value)}
                                            title="Select unit"
                                        >
                                            <option value="">Select Unit</option>
                                            {availableUnits.map((unit) => (
                                                <option key={unit.unitId} value={String(unit.unitId)}>
                                                    {unit.name}
                                                </option>
                                            ))}
                                        </select>
                                        {hasFieldError(sample.id, 'unit') && (
                                            <div className="invalid-feedback">{getFieldError(sample.id, 'unit')}</div>
                                        )}
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label">CFR Min Level</label>
                                        <input
                                            type="text" 
                                            disabled 
                                            readOnly
                                            className="form-control"
                                            value={sample.cfrMinLevel || ""}
                                            onChange={(e) => handleSampleChange(sample.id, "cfrMinLevel", e.target.value)}
                                            title="CFR minimum level"
                                        />
                                    </div>
                                </div>

                                <div className="row g-3 mb-3">
                                    <div className="col-md-4">
                                        <label className="form-label">CFR Max Level</label>
                                        <input
                                            type="text"
                                            disabled 
                                            readOnly
                                            className="form-control"
                                            value={sample.cfrMaxLevel || ""}
                                            onChange={(e) => handleSampleChange(sample.id, "cfrMaxLevel", e.target.value)}
                                            title="CFR maximum level"
                                        />
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label">LOQ Value</label>
                                        <input
                                            type="text"
                                            disabled 
                                            readOnly
                                            className="form-control"
                                            value={sample.loqValue || ""}
                                            onChange={(e) => handleSampleChange(sample.id, "loqValue", e.target.value)}
                                            title="LOQ value"
                                        />
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label">Expected Level <span className="text-danger">*</span></label>
                                        <input
                                            type="text"
                                            className={`form-control ${hasFieldError(sample.id, 'expectedLevel') ? 'is-invalid' : ''}`}
                                            value={sample.expectedLevel || ""}
                                            onChange={(e) => handleSampleChange(sample.id, "expectedLevel", e.target.value)}
                                            onBlur={(e) => validateField(sample.id, "expectedLevel", e.target.value)}
                                            title="Enter expected level"
                                        />
                                        {hasFieldError(sample.id, 'expectedLevel') && (
                                            <div className="invalid-feedback">{getFieldError(sample.id, 'expectedLevel')}</div>
                                        )}
                                    </div>
                                </div>

                                <div className="row g-3 mb-3">
                                    <div className="col-md-4">
                                        <label className="form-label">Phibro Lot No. <span className="text-danger">*</span></label>
                                        <input
                                            type="text"
                                            className={`form-control ${hasFieldError(sample.id, 'phibroLots') ? 'is-invalid' : ''}`}
                                            value={sample.phibroLots || ""}
                                            onChange={(e) => handleSampleChange(sample.id, "phibroLots", e.target.value)}
                                            onBlur={(e) => validateField(sample.id, "phibroLots", e.target.value)}
                                            title="Enter Phibro lot number"
                                        />
                                        {hasFieldError(sample.id, 'phibroLots') && (
                                            <div className="invalid-feedback">{getFieldError(sample.id, 'phibroLots')}</div>
                                        )}
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label">Customer Sample ID <span className="text-danger">*</span></label>
                                        <input
                                            type="text"
                                            className={`form-control ${hasFieldError(sample.id, 'customerSampleId') ? 'is-invalid' : ''}`}
                                            value={sample.customerSampleId || ""}
                                            onChange={(e) => handleSampleChange(sample.id, "customerSampleId", e.target.value)}
                                            onBlur={(e) => validateField(sample.id, "customerSampleId", e.target.value)}
                                            title="Enter customer sample ID"
                                        />
                                        {hasFieldError(sample.id, 'customerSampleId') && (
                                            <div className="invalid-feedback">{getFieldError(sample.id, 'customerSampleId')}</div>
                                        )}
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label">Reference No</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={sample.referenceNo || ""}
                                            onChange={(e) => handleSampleChange(sample.id, "referenceNo", e.target.value)}
                                            placeholder="Enter Reference Number"
                                            title="Enter reference number"
                                        />
                                    </div>
                                </div>

                                <div className="row g-3 mb-4">
                                    <div className="col-12">
                                        <label className="form-label">Remarks</label>
                                        <textarea
                                            className="form-control"
                                            value={sample.remarks || ""}
                                            onChange={(e) => handleSampleChange(sample.id, "remarks", e.target.value)}
                                            rows="4"
                                            placeholder="Enter any additional remarks here..."
                                            title="Enter remarks"
                                        />
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </Form>
    );
};

export default AssayEditPage;
